/**
Cabinet Essentials Kitchen Estimator
Ten-step estimator application and cabinet-generation pricing engine.
 */
(() => {
  'use strict';
  const TOTAL_STEPS = 10;
  const ISLAND_STEP = 6;
  const DOOR_STYLE_STEP = 7;
  const RECOMMENDATION_STEP = 9;
  const CONTACT_STEP = 10;
  const REFRIGERATOR_BRIDGE_WIDTH = 36;
  const REFRIGERATOR_OPENING_WIDTH = 33;
  const RANGE_OPENING_WIDTH = 30;
  const DISHWASHER_OPENING_WIDTH = 24;
  const SINK_BASE_WIDTH = 36;
  const HOOD_CABINET_WIDTH = 30;
  const SINK_WINDOW_OPENING_WIDTH = 36;
  const STANDARD_FILLER_ALLOWANCE = 150;
  const FINISHED_END_PANEL_PRICE = 150;
  const ISLAND_BACK_PANEL_PRICE = 500;
  const PANTRY_24_PRICE = 1600;
  const SLIM_SHAKER_MULTIPLIER = 1.075;
  const CABINET_WIDTHS = [36, 33, 30, 27, 24, 21, 18, 15, 12];
  const ISLAND_CABINET_LAYOUTS = {
    small: [30, 30],
    medium: [36, 24, 24],
    large: [36, 30, 30]
  };
  const BASE_CABINET_PRICES = {
    12: 270,
    15: 300,
    18: 320,
    21: 350,
    24: 390,
    27: 410,
    30: 430,
    33: 460,
    36: 490
  };
  const WALL_30_PRICES = {
    12: 120,
    15: 140,
    18: 150,
    21: 170,
    24: 200,
    27: 220,
    30: 230,
    33: 260,
    36: 270
  };
  const WALL_36_PRICES = {
    12: 140,
    15: 160,
    18: 170,
    21: 190,
    24: 230,
    27: 250,
    30: 260,
    33: 290,
    36: 300
  };
  const STACKED_12_PRICES = {
    12: 90,
    15: 100,
    18: 120,
    21: 130,
    24: 160,
    27: 180,
    30: 260,
    33: 190,
    36: 200
  };
  const SPECIAL_PRICES = {
    sinkBase36: 330,
    hoodCabinet30: 250,
    blindCornerBase: 320,
    wallCorner30: 250,
    wallCorner36: 300,
    wallCorner42: 350,
    stackedWallCorner12: 200
  };
  const REQUIRED_WALLS = {
    'one-wall': ['A'],
    galley: ['A', 'B'],
    'l-shape': ['A', 'B'],
    'u-shape': ['A', 'B', 'C'],
    'not-sure': ['A']
  };
  const CORNER_COUNTS = {
    'one-wall': 0,
    galley: 0,
    'l-shape': 1,
    'u-shape': 2,
    'not-sure': 0
  };
  const LABELS = {
    projectType: {
      'replace-cabinets': 'Replacing Existing Cabinets',
      'kitchen-renovation': 'Renovating My Kitchen',
      'new-kitchen': 'Building a New Kitchen',
      exploring: 'Still Exploring'
    },
    kitchenShape: {
      'one-wall': 'One Wall',
      galley: 'Galley',
      'l-shape': 'L-Shaped',
      'u-shape': 'U-Shaped',
      'not-sure': 'Not Sure'
    },
    hasIsland: {
      yes: 'Yes',
      no: 'No',
      'not-sure': 'Not sure'
    },
    ceilingHeight: {
      '8-ft': "8'",
      '9-ft': "9'",
      '10-ft': "10'",
      'not-sure': 'Not Sure'
    },
    upperCabinetFinish: {
      standard: 'Standard Height Cabinets',
      'to-ceiling': 'Cabinets to the Ceiling',
      'not-sure': 'Not Sure'
    },
    islandSize: {
      small: "Small (4–5')",
      medium: "Medium (6–7')",
      large: "Large (8'+)"
    },
    islandSeating: {
      yes: 'Yes',
      no: 'No',
      'not-sure': 'Not sure'
    },
    doorStyle: {
      shaker: 'Shaker',
      'slim-shaker': 'Slim Shaker'
    },
    investment: {
      'under-10000': 'Under $10,000',
      '10000-15000': '$10,000–15,000',
      '15000-20000': '$15,000–20,000',
      '20000-30000': '$20,000–30,000',
      '30000-plus': 'Over $30,000',
      'not-sure': 'Not Sure'
    },
    timeline: {
      'as-soon-as-possible': 'As soon as possible',
      'within-3-months': 'Within 3 months',
      '3-6-months': '3–6 months',
      '6-12-months': '6–12 months',
      'just-planning': 'Just planning'
    }
  };
  const BUDGET_RANGES = {
    'under-10000': [0, 9999],
    '10000-15000': [10000, 15000],
    '15000-20000': [15000, 20000],
    '20000-30000': [20000, 30000],
    '30000-plus': [30000, Number.POSITIVE_INFINITY]
  };
  class KitchenGuideApp {
    constructor(root) {
      this.root = root;
      this.view = root.querySelector('[data-ce-guide-view]');
      this.progress = root.querySelector('[data-ce-guide-progress]');
      this.progressLabel = root.querySelector('[data-ce-guide-progress-label]');
      this.progressValue = root.querySelector('[data-ce-guide-progress-value]');
  this.state = {
    currentStep: 1,
    answers: {
      projectType: '',
      kitchenShape: '',
      hasIsland: '',
      ceilingHeight: '',
      upperCabinetFinish: '',
      islandSize: '',
      islandSeating: '',
      doorStyle: '',
      investment: '',
      timeline: ''
    },
    measurements: {
      wallAFeet: '',
      wallAInches: '',
      wallBFeet: '',
      wallBInches: '',
      wallCFeet: '',
      wallCInches: ''
    },
    recommendation: null
  };

  this.handleClick = this.handleClick.bind(this);
  this.handleInput = this.handleInput.bind(this);
  this.handleSubmit = this.handleSubmit.bind(this);
}

initialize() {
  if (
    !this.view ||
    !this.progress ||
    !this.progressLabel ||
    !this.progressValue
  ) {
    return;
  }

  this.root.addEventListener('click', this.handleClick);
  this.root.addEventListener('input', this.handleInput);
  this.root.addEventListener('change', this.handleInput);
  this.root.addEventListener('submit', this.handleSubmit);

  const contactTemplate = this.getTemplate(CONTACT_STEP);
  const contactSuccess = contactTemplate
    ? contactTemplate.content.querySelector('[data-ce-contact-success]')
    : null;

  if (contactSuccess) {
    this.state.currentStep = CONTACT_STEP;
  }

  this.render();

  if (contactSuccess) {
    window.requestAnimationFrame(() => {
      const successMessage = this.view.querySelector(
        '[data-ce-contact-success]'
      );

      if (successMessage) {
        successMessage.focus();
      }
    });
  }
}

getTemplate(step) {
  return this.root.querySelector(
    `template[data-ce-guide-step="${step}"]`
  );
}

handleClick(event) {
  const option = event.target.closest('[data-ce-guide-option]');

  if (option && this.root.contains(option)) {
    this.selectOption(option);
    return;
  }

  const action = event.target.closest('[data-ce-guide-action]');

  if (!action || !this.root.contains(action)) {
    return;
  }

  const actionName = action.dataset.ceGuideAction;

  if (actionName === 'next') {
    this.nextStep();
  }

  if (actionName === 'previous') {
    this.previousStep();
  }
}

handleInput(event) {
  const measurement = event.target.closest('[data-ce-guide-measurement]');

  if (!measurement || !this.root.contains(measurement)) {
    return;
  }

  const key = measurement.dataset.ceGuideMeasurement;
  this.state.measurements[key] = measurement.value;
  this.updateNavigationState();
}

handleSubmit() {
  this.populateProjectSummary();
}

selectOption(option) {
  const answerKey = option.dataset.ceGuideAnswer;
  const answerValue = option.dataset.ceGuideValue;

  if (!answerKey || typeof answerValue === 'undefined') {
    return;
  }

  this.state.answers[answerKey] = answerValue;

  if (answerKey === 'hasIsland' && answerValue !== 'yes') {
    this.state.answers.islandSize = '';
    this.state.answers.islandSeating = '';
  }

  const optionGroup = this.view.querySelectorAll(
    `[data-ce-guide-option][data-ce-guide-answer="${answerKey}"]`
  );

  optionGroup.forEach((item) => {
    const isSelected = item.dataset.ceGuideValue === answerValue;
    item.classList.toggle('is-selected', isSelected);
    item.setAttribute('aria-pressed', String(isSelected));
  });

  this.updateNavigationState();
}

render() {
  const template = this.getTemplate(this.state.currentStep);

  if (!template) {
    return;
  }

  this.view.replaceChildren(template.content.cloneNode(true));
  this.restoreCurrentStepValues();

  if (this.state.currentStep === 4) {
    this.updateWallVisibility();
  }

  if (this.state.currentStep === RECOMMENDATION_STEP) {
    this.renderRecommendation();
  }

  if (this.state.currentStep === CONTACT_STEP) {
    this.populateProjectSummary();
  }

  this.updateProgress();
  this.updateNavigationState();
  this.focusCurrentStep();
}

restoreCurrentStepValues() {
  this.view.querySelectorAll('[data-ce-guide-option]').forEach((option) => {
    const answerKey = option.dataset.ceGuideAnswer;
    const answerValue = option.dataset.ceGuideValue;
    const isSelected = this.state.answers[answerKey] === answerValue;

    option.classList.toggle('is-selected', isSelected);
    option.setAttribute('aria-pressed', String(isSelected));
  });

  this.view
    .querySelectorAll('[data-ce-guide-measurement]')
    .forEach((input) => {
      const key = input.dataset.ceGuideMeasurement;
      input.value = this.state.measurements[key] ?? '';
    });
}

updateWallVisibility() {
  const requiredWalls =
    REQUIRED_WALLS[this.state.answers.kitchenShape] || ['A'];

  this.view.querySelectorAll('[data-ce-guide-wall]').forEach((wall) => {
    const wallName = wall.dataset.ceGuideWall;
    const isRequired = requiredWalls.includes(wallName);

    wall.hidden = !isRequired;
    wall.querySelectorAll('input').forEach((input) => {
      input.disabled = !isRequired;
      input.required = isRequired;
    });
  });
}

updateProgress() {
  const step = this.state.currentStep;
  const percentage = (step / TOTAL_STEPS) * 100;
  const progressText = `Step ${step} of ${TOTAL_STEPS}`;

  this.progress.setAttribute('aria-valuemax', String(TOTAL_STEPS));
  this.progress.setAttribute('aria-valuenow', String(step));
  this.progress.setAttribute('aria-valuetext', progressText);
  this.progressLabel.textContent = progressText;
  this.progressValue.style.width = `${percentage}%`;
}

focusCurrentStep() {
  const focusTarget = this.view.querySelector('[data-ce-guide-focus]');

  if (focusTarget) {
    window.requestAnimationFrame(() => {
      focusTarget.focus({ preventScroll: true });
    });
  }

  // Don't scroll on the initial welcome screen.
  if (this.state.currentStep > 1) {
    this.root.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  }
}

updateNavigationState() {
  const nextButton = this.view.querySelector(
    '[data-ce-guide-action="next"]'
  );

  if (nextButton) {
    nextButton.disabled = !this.canAdvance(this.state.currentStep);
  }
}

canAdvance(step) {
  const answers = this.state.answers;

  switch (step) {
    case 1:
      return true;
    case 2:
      return Boolean(answers.projectType);
    case 3:
      return Boolean(answers.kitchenShape && answers.hasIsland);
    case 4:
      return this.measurementsAreValid();
    case 5:
      return Boolean(
        answers.ceilingHeight && answers.upperCabinetFinish
      );
    case 6:
      return answers.hasIsland !== 'yes'
        ? true
        : Boolean(answers.islandSize && answers.islandSeating);
    case 7:
      return Boolean(answers.doorStyle);
    case 8:
      return Boolean(answers.investment && answers.timeline);
    case 9:
      return true;
    default:
      return false;
  }
}

measurementsAreValid() {
  const requiredWalls =
    REQUIRED_WALLS[this.state.answers.kitchenShape] || ['A'];

  return requiredWalls.every((wallName) => {
    const feetKey = `wall${wallName}Feet`;
    const inchesKey = `wall${wallName}Inches`;
    const feetRaw = this.state.measurements[feetKey];
    const inchesRaw = this.state.measurements[inchesKey];
    const feet = Number(feetRaw);
    const inches = inchesRaw === '' ? 0 : Number(inchesRaw);

    return (
      feetRaw !== '' &&
      Number.isFinite(feet) &&
      Number.isFinite(inches) &&
      feet >= 4 &&
      feet <= 40 &&
      inches >= 0 &&
      inches <= 11
    );
  });
}
nextStep() {
  const currentStep = this.state.currentStep;

  if (!this.canAdvance(currentStep)) {
    this.updateNavigationState();
    return;
  }

  if (currentStep === 5) {
    this.state.currentStep =
      this.state.answers.hasIsland === 'yes'
        ? ISLAND_STEP
        : DOOR_STYLE_STEP;
  } else {
    this.state.currentStep = Math.min(
      currentStep + 1,
      TOTAL_STEPS
    );
  }

  if (currentStep === 8) {
    this.state.recommendation = this.calculateRecommendation();
  }

  this.render();
}

previousStep() {
  const currentStep = this.state.currentStep;

  if (currentStep <= 1) {
    return;
  }

  if (currentStep === DOOR_STYLE_STEP) {
    this.state.currentStep =
      this.state.answers.hasIsland === 'yes'
        ? ISLAND_STEP
        : 5;
  } else if (currentStep === ISLAND_STEP) {
    this.state.currentStep = 5;
  } else {
    this.state.currentStep = Math.max(currentStep - 1, 1);
  }

  this.render();
}

getRequiredWalls() {
  return REQUIRED_WALLS[this.state.answers.kitchenShape] || ['A'];
}

getWallInches(wallName) {
  const feet =
    Number(this.state.measurements[`wall${wallName}Feet`]) || 0;
  const inches =
    Number(this.state.measurements[`wall${wallName}Inches`]) || 0;

  return (feet * 12) + inches;
}

getTotalWallInches() {
  return this.getRequiredWalls().reduce(
    (total, wallName) => total + this.getWallInches(wallName),
    0
  );
}

getCornerCount() {
  return CORNER_COUNTS[this.state.answers.kitchenShape] || 0;
}

generateCabinetWidths(runInches) {
  const widths = [];
  let remaining = Math.max(0, Math.floor(runInches));

  CABINET_WIDTHS.forEach((width) => {
    while (remaining >= width) {
      widths.push(width);
      remaining -= width;
    }
  });

  return widths;
}

priceCabinetWidths(widths, priceTable, multiplier = 1) {
  return widths.reduce((total, width) => {
    const cabinetPrice = priceTable[width] || 0;
    return total + (cabinetPrice * multiplier);
  }, 0);
}

sumCabinetWidths(widths) {
  return widths.reduce((total, width) => total + width, 0);
}

getWallCabinetSystem() {
  const ceilingHeight = this.state.answers.ceilingHeight;
  const upperFinish = this.state.answers.upperCabinetFinish;
  const isToCeiling = upperFinish === 'to-ceiling';

  if (ceilingHeight === '8-ft' && upperFinish === 'standard') {
    return {
      primaryPrices: WALL_30_PRICES,
      primaryMultiplier: 1,
      stackedPrices: null,
      cornerPrice: SPECIAL_PRICES.wallCorner30,
      crownRate: 0
    };
  }

  if (ceilingHeight === '8-ft' && isToCeiling) {
    return {
      primaryPrices: WALL_36_PRICES,
      primaryMultiplier: 1,
      stackedPrices: null,
      cornerPrice: SPECIAL_PRICES.wallCorner36,
      crownRate: 35
    };
  }

  if (
    (ceilingHeight === '9-ft' || ceilingHeight === '10-ft') &&
    upperFinish === 'standard'
  ) {
    return {
      primaryPrices: WALL_36_PRICES,
      primaryMultiplier: 1.15,
      stackedPrices: null,
      cornerPrice: SPECIAL_PRICES.wallCorner42,
      crownRate: 0
    };
  }

  if (
    (ceilingHeight === '9-ft' || ceilingHeight === '10-ft') &&
    isToCeiling
  ) {
    return {
      primaryPrices: WALL_36_PRICES,
      primaryMultiplier: 1,
      stackedPrices: STACKED_12_PRICES,
      cornerPrice:
        SPECIAL_PRICES.wallCorner36 +
        SPECIAL_PRICES.stackedWallCorner12,
      crownRate: 50
    };
  }

  return {
    primaryPrices: WALL_36_PRICES,
    primaryMultiplier: 1,
    stackedPrices: null,
    cornerPrice: SPECIAL_PRICES.wallCorner36,
    crownRate: 0
  };
}

priceWallCabinet(width, system) {
  const primaryPrice =
    (system.primaryPrices[width] || 0) *
    system.primaryMultiplier;
  const stackedPrice = system.stackedPrices
    ? system.stackedPrices[width] || 0
    : 0;

  return primaryPrice + stackedPrice;
}

calculateBaseCabinetPrice(totalWallInches, cornerCount) {
  const standardFixedZone =
    REFRIGERATOR_OPENING_WIDTH +
    RANGE_OPENING_WIDTH +
    DISHWASHER_OPENING_WIDTH +
    SINK_BASE_WIDTH;
  const proposedDeduction =
    totalWallInches < 168 ? 90 : standardFixedZone;
  const maximumDeduction = totalWallInches * 0.55;
  const applianceAndSinkDeduction = Math.min(
    proposedDeduction,
    maximumDeduction
  );
  const cornerAllowance = cornerCount * 36;
  const baseRunInches = Math.max(
    0,
    totalWallInches -
      applianceAndSinkDeduction -
      cornerAllowance
  );
  const baseWidths = this.generateCabinetWidths(baseRunInches);

  let price = this.priceCabinetWidths(
    baseWidths,
    BASE_CABINET_PRICES
  );

  price += SPECIAL_PRICES.sinkBase36;
  price += cornerCount * SPECIAL_PRICES.blindCornerBase;
  price += STANDARD_FILLER_ALLOWANCE;
  price += 2 * FINISHED_END_PANEL_PRICE;

  return price;
}

calculateWallCabinetPrice(totalWallInches, cornerCount) {
  const system = this.getWallCabinetSystem();
  const fixedWallZones =
    HOOD_CABINET_WIDTH +
    SINK_WINDOW_OPENING_WIDTH +
    REFRIGERATOR_OPENING_WIDTH;
  const cornerAllowance = cornerCount * 24;
  const wallRunInches = Math.max(
    0,
    totalWallInches -
      fixedWallZones -
      cornerAllowance
  );
  const wallWidths = this.generateCabinetWidths(wallRunInches);

  let price = wallWidths.reduce(
    (total, width) => total + this.priceWallCabinet(width, system),
    0
  );

  price += SPECIAL_PRICES.hoodCabinet30;
  price += this.priceWallCabinet(
    REFRIGERATOR_BRIDGE_WIDTH,
    system
  );
  price += cornerCount * system.cornerPrice;

  if (
    this.state.answers.upperCabinetFinish === 'to-ceiling' &&
    system.crownRate > 0
  ) {
    const upperRunInches =
      this.sumCabinetWidths(wallWidths) +
      REFRIGERATOR_BRIDGE_WIDTH +
      cornerAllowance;

    price += (upperRunInches / 12) * system.crownRate;
  }

  return price;
}

getAssumedPantryCount(totalWallInches) {
  const kitchenShape = this.state.answers.kitchenShape;

  switch (kitchenShape) {
    case 'one-wall':
      if (totalWallInches < 144) {
        return 0;
      }

      return totalWallInches < 192 ? 1 : 2;

    case 'galley':
      if (totalWallInches < 264) {
        return 0;
      }

      return totalWallInches < 336 ? 1 : 2;

    case 'l-shape':
      if (totalWallInches < 264) {
        return 0;
      }

      return totalWallInches < 360 ? 1 : 2;

    case 'u-shape':
      return totalWallInches < 288 ? 1 : 2;

    case 'not-sure':
    default:
      return totalWallInches < 264 ? 0 : 1;
  }
}

calculatePantryPrice(totalWallInches) {
  return (
    this.getAssumedPantryCount(totalWallInches) *
    PANTRY_24_PRICE
  );
}

calculateIslandPrice() {
  if (this.state.answers.hasIsland !== 'yes') {
    return 0;
  }

  const islandWidths =
    ISLAND_CABINET_LAYOUTS[this.state.answers.islandSize] || [];

  return (
    this.priceCabinetWidths(
      islandWidths,
      BASE_CABINET_PRICES
    ) +
    (2 * FINISHED_END_PANEL_PRICE) +
    ISLAND_BACK_PANEL_PRICE
  );
}

calculateEstimatedPrice() {
  const totalWallInches = this.getTotalWallInches();
  const cornerCount = this.getCornerCount();

  let estimatedPrice =
    this.calculateBaseCabinetPrice(
      totalWallInches,
      cornerCount
    ) +
    this.calculateWallCabinetPrice(
      totalWallInches,
      cornerCount
    ) +
    this.calculatePantryPrice(totalWallInches) +
    this.calculateIslandPrice();

  if (this.state.answers.doorStyle === 'slim-shaker') {
    estimatedPrice *= SLIM_SHAKER_MULTIPLIER;
  }

  return estimatedPrice;
}

roundToNearestFiveHundred(value) {
  return Math.round(value / 500) * 500;
}

calculateEstimateRange(estimatedPrice) {
  const low = this.roundToNearestFiveHundred(
    estimatedPrice * 0.94
  );
  const high = this.roundToNearestFiveHundred(
    estimatedPrice * 1.15
  );

  return {
    low,
    high: Math.max(high, low)
  };
}

getBudgetFit(estimateRange) {
  const investment = this.state.answers.investment;

  if (investment === 'not-sure' || !BUDGET_RANGES[investment]) {
    return 'unknown';
  }

  const [budgetLow, budgetHigh] = BUDGET_RANGES[investment];

  if (budgetHigh < estimateRange.low) {
    return 'below';
  }

  if (budgetLow > estimateRange.high) {
    return 'above';
  }

  return 'overlap';
}

getFitMessage(fit) {
  switch (fit) {
    case 'below':
      return "Your selected investment range is below the preliminary estimate for this kitchen.\n\nThis doesn't necessarily mean the project isn't possible.\n\nYou may be able to adjust the design, cabinet configuration or selections to better align with your budget.";
    case 'above':
      return "Your investment range appears well suited for the project you've described.";
    case 'overlap':
      return 'Based on your answers, Cabinet Essentials appears to be an excellent fit for your project.';
    default:
      return 'Based on your answers, Cabinet Essentials appears to be an excellent fit for your project. The preliminary estimate provides a useful starting point for planning.';
  }
}

calculateConfidence() {
  const answers = this.state.answers;
  let uncertaintyCount = 0;

  if (answers.kitchenShape === 'not-sure') {
    uncertaintyCount += 1;
  }

  if (answers.hasIsland === 'not-sure') {
    uncertaintyCount += 1;
  }

  if (answers.ceilingHeight === 'not-sure') {
    uncertaintyCount += 1;
  }

  if (answers.upperCabinetFinish === 'not-sure') {
    uncertaintyCount += 1;
  }

  if (
    answers.hasIsland === 'yes' &&
    answers.islandSeating === 'not-sure'
  ) {
    uncertaintyCount += 1;
  }

  if (answers.investment === 'not-sure') {
    uncertaintyCount += 1;
  }

  if (uncertaintyCount === 0) {
    return {
      level: 'High',
      reason:
        'Your layout, measurements, cabinet height and door selection provide enough detail for a strong preliminary estimate.'
    };
  }

  if (uncertaintyCount <= 2) {
    return {
      level: 'Medium',
      reason:
        'Some project details are still approximate, so the final cabinet cost may vary as your layout and selections are confirmed.'
    };
  }

  return {
    level: 'Low',
    reason:
      'Several project details are still undecided. This estimate is a useful planning range and can be refined as more information becomes available.'
  };
}

calculateRecommendation() {
  const estimatedPrice = this.calculateEstimatedPrice();
  const estimateRange = this.calculateEstimateRange(estimatedPrice);
  const fit = this.getBudgetFit(estimateRange);
  const confidence = this.calculateConfidence();

  return {
    estimatedPrice,
    estimateRange,
    fit,
    fitMessage: this.getFitMessage(fit),
    confidence,
    assumedPantryCount: this.getAssumedPantryCount(
      this.getTotalWallInches()
    )
  };
}

formatCurrency(value) {
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
    maximumFractionDigits: 0
  }).format(value);
}

formatEstimateRange(range) {
  return `${this.formatCurrency(range.low)}–${this.formatCurrency(range.high)}`;
}

getLabel(answerKey) {
  const value = this.state.answers[answerKey];

  return LABELS[answerKey]?.[value] || 'Not provided';
}

formatWallMeasurement(wallName) {
  const feet =
    Number(this.state.measurements[`wall${wallName}Feet`]) || 0;
  const inches =
    Number(this.state.measurements[`wall${wallName}Inches`]) || 0;

  return `${feet}' ${inches}"`;
}

getVisibleSummaryRows() {
  const answers = this.state.answers;
  const measurementSummary = this.getRequiredWalls()
    .map(
      (wallName) =>
        `Wall ${wallName}: ${this.formatWallMeasurement(wallName)}`
    )
    .join(', ');

  const rows = [
    ['Project Type', this.getLabel('projectType')],
    ['Kitchen Shape', this.getLabel('kitchenShape')],
    ['Island', this.getLabel('hasIsland')],
    ['Approximate Measurements', measurementSummary],
    ['Ceiling Height', this.getLabel('ceilingHeight')],
    ['Upper Cabinets', this.getLabel('upperCabinetFinish')]
  ];

  if (answers.hasIsland === 'yes') {
    rows.push(
      ['Island Size', this.getLabel('islandSize')],
      ['Island Seating', this.getLabel('islandSeating')]
    );
  }

  rows.push(
    ['Door Style', this.getLabel('doorStyle')],
    ['Planning Range', this.getLabel('investment')],
    ['Timeline', this.getLabel('timeline')]
  );

  return rows;
}

renderRecommendation() {
  if (!this.state.recommendation) {
    this.state.recommendation = this.calculateRecommendation();
  }

  const recommendation = this.state.recommendation;
  const summary = this.view.querySelector('[data-ce-guide-summary]');
  const estimate = this.view.querySelector('[data-ce-guide-estimate]');
  const confidence = this.view.querySelector(
    '[data-ce-guide-confidence]'
  );
  const confidenceReason = this.view.querySelector(
    '[data-ce-guide-confidence-reason]'
  );
  const fitMessage = this.view.querySelector(
    '[data-ce-guide-fit-message]'
  );

  if (summary) {
    summary.replaceChildren();

    this.getVisibleSummaryRows().forEach(([term, description]) => {
      const termElement = document.createElement('dt');
      const descriptionElement = document.createElement('dd');

      termElement.textContent = term;
      descriptionElement.textContent = description;
      summary.append(termElement, descriptionElement);
    });
  }

  if (estimate) {
    estimate.textContent = this.formatEstimateRange(
      recommendation.estimateRange
    );
  }

  if (confidence) {
    confidence.textContent = recommendation.confidence.level;
  }

  if (confidenceReason) {
    confidenceReason.textContent =
      recommendation.confidence.reason;
  }

  if (fitMessage) {
    fitMessage.textContent = recommendation.fitMessage;
  }
}

getPantrySummaryLabel(count) {
  if (count === 1) {
    return '1 assumed 24" pantry cabinet';
  }

  if (count === 2) {
    return '2 assumed 24" pantry cabinets';
  }

  return 'None assumed';
}

buildProjectSummary() {
  if (!this.state.recommendation) {
    this.state.recommendation = this.calculateRecommendation();
  }

  const recommendation = this.state.recommendation;
  const lines = ['Cabinet Essentials Kitchen Estimator', ''];

  this.getVisibleSummaryRows().forEach(([label, value]) => {
    lines.push(`${label}: ${value}`);
  });

  lines.push(
    `Assumed Pantry Cabinets: ${this.getPantrySummaryLabel(
      recommendation.assumedPantryCount
    )}`,
    `Estimated Cabinet Cost: ${this.formatEstimateRange(
      recommendation.estimateRange
    )}`,
    `Estimate Accuracy: ${recommendation.confidence.level}`,
    `Estimate Accuracy Details: ${recommendation.confidence.reason}`,
    `Budget Comparison: ${recommendation.fitMessage}`
  );

  return lines.join('\n');
}

populateProjectSummary() {
  const summaryField = this.view.querySelector(
    '[data-ce-project-summary]'
  );

  if (summaryField) {
    summaryField.value = this.buildProjectSummary();
  }
}
  }
  const initializeGuide = (root) => {
    if (!root || root.dataset.ceGuideInitialized === 'true') {
      return;
    }
root.dataset.ceGuideInitialized = 'true';
const app = new KitchenGuideApp(root);
app.initialize();
  };
  const initializeAllGuides = (scope = document) => {
    if (scope.matches?.('[data-ce-kitchen-guide]')) {
      initializeGuide(scope);
    }
scope
  .querySelectorAll?.('[data-ce-kitchen-guide]')
  .forEach(initializeGuide);
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initializeAllGuides();
    });
  } else {
    initializeAllGuides();
  }
  document.addEventListener('shopify:section:load', (event) => {
    initializeAllGuides(event.target);
  });
})();